import React, { useEffect, useRef, useState } from "react";
import "./expirationDateComponent.scss";

export const ExpirationDateComponent = React.forwardRef((props, ref) => {
  const { name, disabled, read_only } = props;
  const inputRef = useRef(null);

  // Format date to YYYY-MM-DD if it exists
  const formatDate = (date) => {
    if (!date) return "";
    
    // If already in YYYY-MM-DD format, return as-is
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    try {
      // Normalize date string - remove ordinal suffixes (st, nd, rd, th)
      let normalizedDate = String(date).trim();
      normalizedDate = normalizedDate.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
      
      // Try parsing the date
      const parsedDate = new Date(normalizedDate);
      
      // Check if date is valid
      if (isNaN(parsedDate.getTime())) {
        console.warn("Invalid date value:", date);
        return "";
      }
      
      // Format to YYYY-MM-DD
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error("Date formatting error:", e, "for date:", date);
      return "";
    }
  };

  // Get the initial value from various possible locations
  const getInitialValue = () => {
    const value =
      props.value ||
      props.defaultValue ||
      (props.data && (props.data.value || props.data.defaultValue)) ||
      "";
    return formatDate(value);
  };

  const [dateValue, setDateValue] = useState(getInitialValue());

  useEffect(() => {
    if (ref) {
      ref.current = inputRef.current;
    }
  }, [ref]);

  // Update dateValue when props change (for controlled component behavior)
  useEffect(() => {
    const newValue = getInitialValue();
    setDateValue(newValue);
  }, [props.defaultValue, props.value, props.data?.defaultValue, props.data?.value]);

  const formatLabel = (label) => {
    return label || "";
  };

  const getComponentName = () => {
    // Get the component name from the parent's label if available
    const parentLabel = props.data?.parentProps?.data?.label;
    if (parentLabel) {
      // Remove any HTML tags and trim the result
      return parentLabel.replace(/<\/?[^>]+(>|$)/g, "").trim();
    }
    return "";
  };

  const handleDateChange = (e) => {
    const newValue = e.target.value;
    setDateValue(newValue);
    if (props.onChange) {
      props.onChange(name, newValue);
    }
  };

  const componentName = getComponentName();
  const labelText = componentName
    ? `${componentName} Expiration Date`
    : "Expiration Date";

  return (
    <div className="modern-date-input">
      <label className="date-label">
        <span
          dangerouslySetInnerHTML={{
            __html: formatLabel(labelText),
          }}
        ></span>
        {props.data?.required ? (
          <span className="required-mark">*</span>
        ) : (
          <></>
        )}
      </label>
      <div className="date-input-wrapper form-group">
        <input
          ref={inputRef}
          type="date"
          className="modern-date-control"
          name={name}
          disabled={disabled}
          readOnly={read_only}
          value={dateValue}
          onChange={handleDateChange}
          required={props.data?.required}
        />
        <div className="date-icon">📅</div>
      </div>
    </div>
  );
});
